// constants
import { REPEAT_VALUES } from 'constants/kpi'
// utils
import moment from 'moment'
import { filter, find, get, isEmpty, map, orderBy, pipe } from 'lodash/fp'
import { generateKPIRecurrenceRanges } from './recurrence'
import { safeGetOr } from '../fp'

export const generateRanges = ({ recurrence = {}, nextStartPoint: startPoint }) => {
  let nextStartPoint = startPoint || moment(recurrence.firstPeriodStartsAt)

  let result = {
    latest: { nextStartPoint },
    ranges: []
  }

  let isFutureLimitReached

  const recurrenceEndsAt = recurrence.recurrenceEndsAt ? moment(recurrence.recurrenceEndsAt) : null

  const isWeekly = recurrence.repeatEveryType === REPEAT_VALUES.WEEKLY

  do {
    if (isEmpty(recurrence)) {
      break
    }

    const latest = generateKPIRecurrenceRanges(recurrence, result.latest.nextStartPoint)

    if (!latest) {
      break
    }

    result.latest = latest

    if (recurrenceEndsAt) {
      isFutureLimitReached = result.latest.lastDayOfThePeriod.isAfter(recurrenceEndsAt, 'd')

      if (isFutureLimitReached) {
        break
      }
    }

    result.ranges = [...result.ranges, latest]

    if (!recurrenceEndsAt) {
      const isBeforeFuture = moment()
        .add(1, isWeekly ? 'month' : 'year')
        .isAfter(result.latest.nextStartPoint, 'd')

      isFutureLimitReached = !isBeforeFuture
    }
  } while (!isFutureLimitReached)

  return result
}

export const getResultTargetsByEntityAndTargetId = entity => {
  const areStaticTargets = !get('resultTarget.differentTargets', entity)

  if (areStaticTargets) {
    return () => safeGetOr([], 'resultTarget.results')(entity)
  }

  const differentTargets = safeGetOr([], 'resultTarget.differentResults')(entity)

  return range => {
    const exactEntity = find(
      ({ id, lastDayOfThePeriod }) =>
        range.id === id && moment(range.lastDayOfThePeriod).isSame(moment(lastDayOfThePeriod), 'day')
    )(differentTargets)

    return get('results', exactEntity)
  }
}

export const generatePeriods = planKPI => {
  const capturedResults = safeGetOr([], 'resultTarget.capturedResults')(planKPI)
  const getResultsById = getResultTargetsByEntityAndTargetId(planKPI)

  const { ranges } = generateRanges({ recurrence: planKPI.recurrence })

  const results = pipe(
    orderBy(['id'], ['desc']),
    map(range => {
      const capturedResult = capturedResults.find(
        ({ id, lastDayOfThePeriod }) =>
          id === range.id && moment(range.lastDayOfThePeriod).isSame(moment(lastDayOfThePeriod), 'day')
      )

      return {
        ...range,
        results: getResultsById(range),
        capturedResult
      }
    })
  )(ranges)

  const capturedResultsOutOfRange = pipe(
    filter(
      capturedResult =>
        !results.some(
          ({ id, lastDayOfThePeriod }) =>
            capturedResult.id === id &&
            moment(capturedResult.lastDayOfThePeriod).isSame(moment(lastDayOfThePeriod), 'day')
        )
    ),
    map(capturedResult => ({
      ...capturedResult,
      capturedResult: { value: capturedResult.value },
      results: getResultsById(capturedResult.id)
    }))
  )(capturedResults)

  const currentPeriods = filter(
    ({ dueDate, lastDayOfThePeriod }) =>
      lastDayOfThePeriod.isBefore(moment(), 'day') && !dueDate.isSame(moment(), 'day')
  )(results)

  const todayPeriods = results.filter(({ dueDate }) => dueDate.isSame(moment(), 'day'))

  const comingFirstPeriod = pipe(
    orderBy(({ startPoint }) => startPoint.unix(), 'asc'),
    find(
      ({ dueDate, lastDayOfThePeriod }) =>
        lastDayOfThePeriod.isAfter(moment(), 'day') ||
        (lastDayOfThePeriod.isSame(moment(), 'day') && dueDate.isAfter(moment(), 'day'))
    ),
    period => period || {}
  )(results)

  return { comingFirstPeriod, todayPeriods, currentPeriods, capturedResultsOutOfRange, ranges }
}
